import React from 'react';
import { motion } from 'framer-motion';
interface SplineSceneProps {
  className?: string;
}
/**
 * Custom circular orbital animation — replaces the Spline embed entirely.
 * Pure SVG + Framer Motion. Stays perfectly circular and on-brand.
 */
export function SplineScene({
  className = ''
}: SplineSceneProps) {
  const orbits = [{
    rx: 260,
    ry: 70,
    rotate: 0,
    dur: 22,
    opacity: 0.4,
    particles: 3
  }, {
    rx: 240,
    ry: 100,
    rotate: 30,
    dur: 18,
    opacity: 0.5,
    particles: 4
  }, {
    rx: 280,
    ry: 50,
    rotate: -25,
    dur: 30,
    opacity: 0.3,
    particles: 2
  }, {
    rx: 220,
    ry: 130,
    rotate: 60,
    dur: 25,
    opacity: 0.4,
    particles: 3
  }];
  return <div className={`relative w-full h-full flex items-center justify-center overflow-hidden ${className}`} data-id="element-847">
      {/* Outermost concentric breathing rings */}
      {[0, 1, 2, 3, 4].map(i => <motion.div key={`pulse-${i}`} className="absolute rounded-full border border-[#1a8fd4]/15" style={{
      width: 200,
      height: 200
    }} animate={{
      scale: [1, 4.5],
      opacity: [0.6, 0]
    }} transition={{
      duration: 6,
      repeat: Infinity,
      delay: i * 1.2,
      ease: 'easeOut'
    }} data-id="element-848" />)}

      {/* Static concentric rings for structure */}
      {[1, 2, 3].map(i => <div key={`ring-${i}`} className="absolute rounded-full border border-[#1a8fd4]/10" style={{
      width: 280 * i,
      height: 280 * i
    }} data-id="element-849" />)}

      {/* 3D-tilted orbital rings with traveling particles */}
      <div className="absolute inset-0 flex items-center justify-center" style={{
      perspective: '1000px'
    }} data-id="element-850">
        {orbits.map((orbit, idx) => <motion.div key={`orbit-${idx}`} className="absolute flex items-center justify-center" style={{
        width: orbit.rx * 2,
        height: orbit.ry * 2,
        transform: `rotateZ(${orbit.rotate}deg) rotateX(70deg)`,
        transformStyle: 'preserve-3d'
      }} animate={{
        rotateY: 360
      }} transition={{
        duration: orbit.dur,
        repeat: Infinity,
        ease: 'linear'
      }} data-id="element-851">
            <div className="absolute inset-0 rounded-full border-[1.5px]" style={{
          borderColor: `rgba(26,143,212,${orbit.opacity})`,
          boxShadow: `0 0 24px rgba(26,143,212,${orbit.opacity * 0.4}) inset`
        }} data-id="element-852" />
            {Array.from({
          length: orbit.particles
        }).map((_, p) => {
          const angle = 360 / orbit.particles * p;
          return <div key={`particle-${idx}-${p}`} className="absolute" style={{
            width: 8,
            height: 8,
            left: '50%',
            top: '50%',
            transform: `translate(-50%, -50%) rotate(${angle}deg) translateX(${orbit.rx}px)`
          }} data-id="element-853">
                  <div className="w-2 h-2 rounded-full bg-[#1a8fd4]" style={{
              boxShadow: '0 0 12px #1a8fd4, 0 0 24px rgba(26,143,212,0.6)'
            }} data-id="element-854" />
                </div>;
        })}
          </motion.div>)}
      </div>

      {/* Radar sweep — conic gradient that rotates */}
      <motion.div className="absolute rounded-full" style={{
      width: 600,
      height: 600,
      background: 'conic-gradient(from 0deg, transparent 0%, transparent 75%, rgba(26,143,212,0.15) 95%, rgba(26,143,212,0.5) 100%)'
    }} animate={{
      rotate: 360
    }} transition={{
      duration: 8,
      repeat: Infinity,
      ease: 'linear'
    }} data-id="element-855" />

      {/* Central orb (the periscope "eye") */}
      <div className="absolute flex items-center justify-center" data-id="element-856">
        <motion.div className="absolute rounded-full bg-[#1a8fd4]/20 blur-3xl" style={{
        width: 220,
        height: 220
      }} animate={{
        scale: [1, 1.2, 1],
        opacity: [0.4, 0.7, 0.4]
      }} transition={{
        duration: 4,
        repeat: Infinity,
        ease: 'easeInOut'
      }} data-id="element-857" />
        <motion.div className="absolute rounded-full bg-[#1a8fd4]/40 blur-2xl" style={{
        width: 140,
        height: 140
      }} animate={{
        scale: [1, 1.15, 1],
        opacity: [0.6, 1, 0.6]
      }} transition={{
        duration: 3,
        repeat: Infinity,
        ease: 'easeInOut'
      }} data-id="element-858" />
        <div className="relative w-24 h-24 rounded-full" style={{
        background: 'radial-gradient(circle at 30% 30%, #6cc4ff 0%, #1a8fd4 40%, #0a4f7a 100%)',
        boxShadow: '0 0 60px #1a8fd4, 0 0 120px rgba(26,143,212,0.5), inset 0 0 20px rgba(255,255,255,0.2)'
      }} data-id="element-859">
          <div className="absolute rounded-full bg-white/40 blur-md" style={{
          width: 24,
          height: 24,
          top: '20%',
          left: '25%'
        }} data-id="element-860" />
          <div className="absolute inset-3 rounded-full border border-white/20" data-id="element-861" />
        </div>

        {/* Crosshair lines emanating */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none" data-id="element-862">
          {[0, 90, 180, 270].map(deg => <div key={deg} className="absolute" style={{
          width: 280,
          height: 1,
          background: 'linear-gradient(90deg, transparent, rgba(26,143,212,0.4) 30%, rgba(26,143,212,0.4) 70%, transparent)',
          transform: `rotate(${deg}deg)`
        }} data-id="element-863" />)}
        </div>
      </div>
    </div>;
}